import React, { useState, useEffect, useRef } from 'react';
import './PrivacyPolicyPage.css';
import Logo from './Logo';
import Footer from './Footer';

const PrivacyPolicyPage = ({ onBack, onNavigateToLogin, onNavigateToSignup, onNavigateToAbout, onNavigateToTerms }) => {
  const [activeSection, setActiveSection] = useState('summary');
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const [showMobileProfileMenu, setShowMobileProfileMenu] = useState(false);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      try {
        if (isScrollingRef.current) return;
        if (typeof document === 'undefined' || typeof window === 'undefined') return;

        const sections = document.querySelectorAll('.pp-section');
        let current = 'summary';

        // Check if we're at the bottom of the page
        const isAtBottom = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 10;

        if (isAtBottom && sections.length > 0) {
          // Set to the last section
          const lastSection = sections[sections.length - 1];
          if (lastSection) {
            current = lastSection.getAttribute('id') || 'summary';
          }
        } else {
          sections.forEach((section) => {
            try {
              const sectionTop = section.offsetTop;
              if (window.pageYOffset >= sectionTop - 200) {
                const id = section.getAttribute('id');
                if (id) {
                  current = id;
                }
              }
            } catch (error) {
              // Silently handle section processing errors
            }
          });
        }

        setActiveSection(current);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in scroll handler:', error);
        }
      }
    };

    try {
      if (typeof window !== 'undefined') {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
          try {
            if (typeof window !== 'undefined') {
              window.removeEventListener('scroll', handleScroll);
            }
          } catch (error) {
            // Silently handle cleanup errors
          }
        };
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up scroll listener:', error);
      }
      return () => {};
    }
  }, []);

  // Scroll to top on mobile when component mounts
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        // Check if we're on mobile (max-width: 600px)
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
        
        if (isMobile) {
          // Use requestAnimationFrame to ensure DOM is ready
          const scrollToTop = () => {
            try {
              if (window.scrollTo) {
                window.scrollTo({ top: 0, behavior: 'instant' });
              }
              // Also try scrolling the document element and body
              if (document.documentElement) {
                document.documentElement.scrollTop = 0;
              }
              if (document.body) {
                document.body.scrollTop = 0;
              }
            } catch (error) {
              // Silently handle scroll errors
            }
          };
          
          // Try immediately
          scrollToTop();
          
          // Also try after a short delay to ensure page is fully rendered
          setTimeout(scrollToTop, 0);
          setTimeout(scrollToTop, 50);
          setTimeout(scrollToTop, 100);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error scrolling to top on mobile:', error);
      }
    }
  }, []); // Run once when component mounts

  // Auto-scroll sidebar to keep active item visible
  useEffect(() => {
    try {
      if (sidebarRef.current) {
        const activeButton = sidebarRef.current.querySelector('.pp-nav-item.active');
        if (activeButton) {
          try {
            activeButton.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            });
          } catch (error) {
            // Silently handle scrollIntoView errors
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in sidebar scroll effect:', error);
      }
    }
  }, [activeSection]);

  // Close mobile menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      try {
        if (!e || !e.target) return;
        
        if (showMobileProfileMenu && !e.target.closest('.mobile-profile-icon') && !e.target.closest('.mobile-profile-menu')) {
          setShowMobileProfileMenu(false);
        }
        if (showMobileNavMenu && !e.target.closest('.mobile-hamburger') && !e.target.closest('.mobile-nav-dropdown')) {
          setShowMobileNavMenu(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in click outside handler:', error);
        }
      }
    };

    try {
      document.addEventListener('click', handleClickOutside);
      return () => {
        try {
          document.removeEventListener('click', handleClickOutside);
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up click outside listener:', error);
      }
      return () => {};
    }
  }, [showMobileProfileMenu, showMobileNavMenu]);

  const scrollToSection = (sectionId) => {
    try {
      if (!sectionId) return;
      
      setActiveSection(sectionId);
      isScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);

      if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        const element = document.getElementById(sectionId);
        if (element && window.scrollTo) {
          try {
            const yOffset = -100;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          } catch (error) {
            // Silently handle scroll errors
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in scrollToSection:', error);
      }
    }
  };

  const sections = [
    { id: 'summary', title: 'SUMMARY OF KEY POINTS' },
    { id: 'information-collect', title: '1. WHAT INFORMATION DO WE COLLECT?' },
    { id: 'process-information', title: '2. HOW DO WE PROCESS YOUR INFORMATION?' },
    { id: 'share-information', title: '3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?' },
    { id: 'cookies-tracking', title: '4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?' },
    { id: 'social-logins', title: '5. HOW DO WE HANDLE YOUR SOCIAL LOGINS?' },
    { id: 'international-transfers', title: '6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?' },
    { id: 'information-retention', title: '7. HOW LONG DO WE KEEP YOUR INFORMATION?' },
    { id: 'minors', title: '8. DO WE COLLECT INFORMATION FROM MINORS?' },
    { id: 'privacy-rights', title: '9. WHAT ARE YOUR PRIVACY RIGHTS?' },
    { id: 'do-not-track', title: '10. CONTROLS FOR DO-NOT-TRACK FEATURES' },
    { id: 'updates', title: '11. DO WE MAKE UPDATES TO THIS NOTICE?' },
    { id: 'contact', title: '12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?' },
    { id: 'review-data', title: '13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?' }
  ];

  return (
    <div className="pp-page">
      {/* Navigation Bar */}
      <nav className="navigation">
        <div className="container">
          <div className="nav-content">
            {/* Mobile Hamburger Menu */}
            <div className="mobile-nav-controls">
              <button
                className={`mobile-hamburger ${showMobileNavMenu ? 'active' : ''}`}
                onClick={() => setShowMobileNavMenu(!showMobileNavMenu)}
                aria-label="Navigation menu"
              >
                {/* Hamburger lines */}
                <span></span>
                <span></span>
                <span></span>
                {/* Logo icon - appears when active (mobile only) */}
                <img 
                  className="hamburger-logo-icon"
                  src="/Images/holologo_mobile.svg"
                  alt="DermaMatch Logo"
                />
              </button>
            </div>

            <div className="nav-logo">
              <button
                className="logo-button"
                onClick={onBack}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Logo size="medium" />
              </button>
            </div>
            <div className="nav-items">
              <button onClick={onBack} className="nav-link">DISCOVER</button>
              <button onClick={onNavigateToAbout} className="nav-link">ABOUT</button>
            </div>
            <div className="nav-actions">
              <button onClick={onNavigateToLogin} className="nav-btn btn-secondary">LOG IN</button>
              <button onClick={onNavigateToSignup} className="nav-btn btn-primary">SIGN UP</button>
            </div>

            {/* Mobile Profile Icon */}
            <button
              className="mobile-profile-icon"
              onClick={() => setShowMobileProfileMenu(!showMobileProfileMenu)}
              aria-label="Profile menu"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNavMenu && (
            <div className="mobile-nav-dropdown">
              <button
                onClick={() => { onBack(); setShowMobileNavMenu(false); }}
              >
                DISCOVER
              </button>
              <button
                onClick={() => { onNavigateToAbout(); setShowMobileNavMenu(false); }}
              >
                ABOUT
              </button>
            </div>
          )}

          {/* Mobile Profile Menu */}
          {showMobileProfileMenu && (
            <div className="mobile-profile-menu">
              <button onClick={() => { onNavigateToSignup(); setShowMobileProfileMenu(false); }}>
                SIGN UP
              </button>
              <button onClick={() => { onNavigateToLogin(); setShowMobileProfileMenu(false); }}>
                LOG IN
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="pp-container">
        {/* Sidebar Navigation */}
        <aside className="pp-sidebar" ref={sidebarRef}>
          <nav className="pp-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`pp-nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="pp-content">
          <h1 className="pp-title">Privacy Policy</h1>
          <p className="pp-updated">Last updated July 03, 2025</p>

          <section id="summary" className="pp-section">
            <p>This Privacy Notice for DermaMatch AI, describes how and why we might access, collect, store, use, and/or share ("<strong>process</strong>") your personal information when you use our services ("<strong>Services</strong>"), including when you:</p>
            <p><strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services.</p>

            <h2>SUMMARY OF KEY POINTS</h2>
            <p><em>This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.</em></p>

            <h3>What personal information do we process?</h3>
            <p>When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use. Learn more about personal information you disclose to us.</p>

            <h3>Do we process any sensitive personal information?</h3>
            <p>Some of the information may be considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information.</p>

            <h3>Do we collect any information from third parties?</h3>
            <p>We may collect information from public databases, marketing partners, social media platforms, and other outside sources. Learn more about information collected from other sources.</p>

            <h3>How do we process your information?</h3>
            <p>We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Learn more about how we process your information.</p>

            <h3>In what situations and with which parties do we share personal information?</h3>
            <p>We may share information in specific situations and with specific third parties. Learn more about when and with whom we share your personal information.</p>

            <h3>What are your rights?</h3>
            <p>Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information. Learn more about your privacy rights.</p>

            <h3>How do you exercise your rights?</h3>
            <p>The easiest way to exercise your rights is by submitting a data subject access request, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.</p>

            <p>Want to learn more about what we do with any information we collect? Review the Privacy Notice in full.</p>
          </section>

          <section id="information-collect" className="pp-section">
            <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>

            <h3>Personal information you disclose to us</h3>
            <p><em>In Short: We collect personal information that you provide to us.</em></p>
            <p>We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.</p>
            <p><strong>Sensitive Information.</strong> We do not process sensitive information.</p>
            <p>All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.</p>

            <h3>Information automatically collected</h3>
            <p><em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em></p>
            <p>We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information. This information is primarily needed to maintain the security and operation of our Services, and for our internal analytics and reporting purposes.</p>
            <p>Like many businesses, we also collect information through cookies and similar technologies.</p>
          </section>

          <section id="process-information" className="pp-section">
            <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
            <p><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</em></p>
            <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
          </section>

          <section id="share-information" className="pp-section">
            <h2>3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
            <p><em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em></p>
            <p>We may need to share your personal information in the following situations:</p>
            <ul>
              <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
              <li><strong>Affiliates.</strong> We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Notice. Affiliates include our parent company and any subsidiaries, joint venture partners, or other companies that we control or that are under common control with us.</li>
              <li><strong>Business Partners.</strong> We may share your information with our business partners to offer you certain products, services, or promotions.</li>
            </ul>
          </section>

          <section id="cookies-tracking" className="pp-section">
            <h2>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
            <p><em>In Short: We may use cookies and other tracking technologies to collect and store your information.</em></p>
            <p>We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. Some online tracking technologies help us maintain the security of our Services, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.</p>
            <p>We also permit third parties and service providers to use online tracking technologies on our Services for analytics and advertising, including to help manage and display advertisements, to tailor advertisements to your interests, or to send abandoned shopping cart reminders (depending on your communication preferences). The third parties and service providers use their technology to provide advertising about products and services tailored to your interests which may appear either on our Services or on other websites.</p>
            <p>Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Notice.</p>
          </section>

          <section id="social-logins" className="pp-section">
            <h2>5. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</h2>
            <p><em>In Short: If you choose to register or log in to our Services using a social media account, we may have access to certain information about you.</em></p>
            <p>Our Services offer you the ability to register and log in using your third-party account details (like your Gmail, Apple ID, or Outlook). Where you choose to do this, we will receive certain profile information about you from your social media provider. The profile information we receive may vary depending on the social media provider concerned, but will often include your name, email address, friends list, and profile picture, as well as other information you choose to make public on such a social media platform.</p>
            <p>We will use the information we receive only for the purposes that are described in this Privacy Notice or that are otherwise made clear to you on the relevant Services. Please note that we do not control, and are not responsible for, other uses of your personal information by your third-party social media provider. We recommend that you review their privacy notice to understand how they collect, use, and share your personal information, and how you can set your privacy preferences on their sites and apps.</p>
          </section>

          <section id="international-transfers" className="pp-section">
            <h2>6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</h2>
            <p><em>In Short: We may transfer, store, and process your information in countries other than your own.</em></p>
            <p>Our servers are located in. If you are accessing our Services from outside, please be aware that your information may be transferred to, stored by, and processed by us in our facilities and in the facilities of the third parties with whom we may share your personal information (see "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?" above), in and other countries.</p>
            <p>If you are a resident in the European Economic Area (EEA), United Kingdom (UK), or Switzerland, then these countries may not necessarily have data protection laws or other similar laws as comprehensive as those in your country. However, we will take all necessary measures to protect your personal information in accordance with this Privacy Notice and applicable law.</p>
          </section>

          <section id="information-retention" className="pp-section">
            <h2>7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
            <p><em>In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>
            <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).</p>
            <p>When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.</p>
          </section>

          <section id="minors" className="pp-section">
            <h2>8. DO WE COLLECT INFORMATION FROM MINORS?</h2>
            <p><em>In Short: We do not knowingly collect data from or market to children under 18 years of age.</em></p>
            <p>We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at <a href="mailto:team@dermamatch.ai">team@dermamatch.ai</a>.</p>
          </section>

          <section id="privacy-rights" className="pp-section">
            <h2>9. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
            <p><em>In Short: You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.</em></p>

            <h3>Withdrawing your consent</h3>
            <p>If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.</p>
            <p>However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.</p>

            <h3>Account Information</h3>
            <p>If you would at any time like to review or change the information in your account or terminate your account, you can:</p>
            <p>Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.</p>
          </section>

          <section id="do-not-track" className="pp-section">
            <h2>10. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
            <p>Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.</p>
          </section>

          <section id="updates" className="pp-section">
            <h2>11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
            <p><em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>
            <p>We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.</p>
          </section>

          <section id="contact" className="pp-section">
            <h2>12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
            <p>If you have questions or comments about this notice, you may contact us by email at:</p>
            <p className="pp-contact">
              <strong>DermaMatch AI</strong><br />
              Berkeley, CA<br />
              United States<br />
              <a href="mailto:team@dermamatch.ai">team@dermamatch.ai</a>
            </p>
          </section>

          <section id="review-data" className="pp-section">
            <h2>13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
            <p>Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please contact us by email at <a href="mailto:team@dermamatch.ai">team@dermamatch.ai</a>.</p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <Footer
        onNavigateToHome={onBack}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      />
    </div>
  );
};

export default PrivacyPolicyPage;
